import { useTranslation } from 'react-i18next';

// Styles
import styles from './TablePaginationPages.module.css';

// Components
import Picto from '../../atoms/Picto/Picto';
import TablePaginationPageButton from '../../atoms/TablePaginationPageButton/TablePaginationPageButton';

// Use cases
import {
  calculatePaginationRange,
  PAGINATION_ELLIPSIS_LEFT,
  PAGINATION_ELLIPSIS_RIGHT,
} from '../../../domain/tablePagination/tablePaginationUseCase';

/**
 * Classic numbered pagination control:
 *   [‹ Prev] 1 … 21 [22] 23 … 596 [Next ›]
 *
 * The component is presentational only: it computes the visible page list from
 * `currentPage`/`totalPages` and delegates navigation to the parent through
 * `onPageChange`.
 * @param {object} props - Component props
 * @param {number} props.currentPage - 1-based current page
 * @param {number} props.totalPages - Total number of pages
 * @param {(page: number) => void} props.onPageChange - Called with the requested page
 * @param {boolean} [props.isLoading] - Disables every button (e.g. during a refetch)
 * @param {number} [props.siblingCount=1] - Pages displayed on each side of `currentPage`
 * @param {number} [props.boundaryCount=1] - Pages always displayed at the start/end
 * @returns {JSX.Element|null} The pagination control, or `null` when `totalPages <= 0`
 */
function TablePaginationPages({
  currentPage,
  totalPages,
  onPageChange,
  isLoading = false,
  siblingCount = 1,
  boundaryCount = 1,
}) {
  const { t } = useTranslation();

  if (!Number.isFinite(totalPages) || totalPages <= 0) return null;

  const safeCurrent = Math.min(Math.max(1, Math.floor(currentPage) || 1), totalPages);
  const items = calculatePaginationRange(safeCurrent, totalPages, siblingCount, boundaryCount);

  const isFirst = safeCurrent <= 1;
  const isLast = safeCurrent >= totalPages;

  const goTo = (/** @type {number} */ page) => {
    if (isLoading) return;
    const clamped = Math.min(Math.max(1, page), totalPages);
    if (clamped !== safeCurrent) onPageChange(clamped);
  };

  return (
    <div className={styles['table-pagination-pages']} role="navigation" aria-label={t('pagination.navigationLabel')}>
      <TablePaginationPageButton
        variant="arrow"
        value={safeCurrent - 1}
        onClick={() => goTo(safeCurrent - 1)}
        disabled={isLoading || isFirst}
        ariaLabel={t('pagination.previous')}
      >
        <span className={styles['icon-button']}>
          <Picto icon="chevron-left" />
        </span>
      </TablePaginationPageButton>

      {items.map((item, index) => {
        if (item === PAGINATION_ELLIPSIS_LEFT || item === PAGINATION_ELLIPSIS_RIGHT) {
          return (
            <TablePaginationPageButton
              // eslint-disable-next-line react/no-array-index-key -- ellipsis position is stable for a given range
              key={`${item}-${index}`}
              isEllipsis
            >
              …
            </TablePaginationPageButton>
          );
        }

        return (
          <TablePaginationPageButton
            key={item}
            value={item}
            isActive={item === safeCurrent}
            disabled={isLoading}
            onClick={goTo}
            ariaLabel={t('pagination.goToPage', { page: item })}
          >
            {item}
          </TablePaginationPageButton>
        );
      })}

      <TablePaginationPageButton
        variant="arrow"
        value={safeCurrent + 1}
        onClick={() => goTo(safeCurrent + 1)}
        disabled={isLoading || isLast}
        ariaLabel={t('pagination.next')}
      >
        <span className={styles['icon-button']}>
          <Picto icon="chevron-right" />
        </span>
      </TablePaginationPageButton>
    </div>
  );
}

export default TablePaginationPages;
